/**
 * Kiosk PIN policy — the digit rules, in ONE place.
 *
 * The API validates against this (SetPinDto) and the kiosk/admin UIs shape their
 * inputs from it, so a change to the floor cannot leave the two disagreeing: a UI
 * that accepted 4 digits while the API demanded 6 would fail the employee at the
 * keypad with no useful reason.
 *
 * WHY 4 (Michael, 2026-09-01). Employees keep the PINs they already have from the
 * current time clock, which are four digits. A floor of 6 would make that import
 * impossible — every existing PIN would be rejected — so the floor is 4.
 *
 * This was briefly raised to 6 for audit #7 and is now REVERTED. Be honest about
 * what that costs: four digits is a 10,000-value space, the punch endpoint takes
 * PIN-ONLY credentials (no employee id is entered), so any hit authenticates
 * SOMEBODY, and with a few hundred employees a meaningful fraction of that space is
 * live. Audit #7's length recommendation is therefore knowingly NOT met.
 *
 * What actually bounds guessing here is the throttling, not the length: the
 * per-kiosk wrong-PIN lockout (K2/0016) and the tenant-wide budget (K4/0041) cap
 * attempts per window, and the keyed lookup hash (K3/0040) means a wrong PIN is
 * cheap to reject rather than a denial-of-service lever. Those carry the weight a
 * longer PIN would otherwise have carried — so do NOT weaken them to compensate for
 * anything, and revisit the length if the lockout thresholds are ever loosened.
 *
 * Eight stays the ceiling.
 */
/** Fewest digits a PIN may have. 4, to match the PINs employees already have. */
export const PIN_MIN_DIGITS = 4;
/** Most digits a PIN may have. */
export const PIN_MAX_DIGITS = 8;
/**
 * The one regex both sides validate with. Anchored, digits only — a PIN with a
 * space or a leading '+' must not squeak through into the hash.
 *
 * A LITERAL, deliberately NOT built with `new RegExp` from a template literal.
 * Doing that needs a DOUBLED backslash, and with a single one the `d` escape is
 * not recognised and quietly degrades to a bare letter — producing /^d{6,8}$/,
 * which accepts "dddddd" and rejects every real PIN. That exact mistake shipped
 * here once and made `isValidPin` return false for everything, which is a silent
 * failure: no PIN could be set, and nothing said why. A literal cannot express
 * it. {@link assertPinPolicyConsistent} ties the literal back to the constants.
 */
export const PIN_PATTERN = /^\d{4,8}$/;
/**
 * Prove the literal above still agrees with the two constants, which it hard-codes.
 *
 * Exported for the tests rather than run at import time on purpose: a throw during
 * module load would take the whole API down at boot instead of failing one build.
 */
export function assertPinPolicyConsistent() {
    const atMin = '0'.repeat(PIN_MIN_DIGITS);
    const atMax = '0'.repeat(PIN_MAX_DIGITS);
    if (!PIN_PATTERN.test(atMin) || !PIN_PATTERN.test(atMax)) {
        throw new Error('PIN_PATTERN does not accept the PIN_MIN_DIGITS/PIN_MAX_DIGITS bounds');
    }
    const belowMin = '0'.repeat(PIN_MIN_DIGITS - 1);
    const aboveMax = '0'.repeat(PIN_MAX_DIGITS + 1);
    if (PIN_PATTERN.test(belowMin) || PIN_PATTERN.test(aboveMax)) {
        throw new Error('PIN_PATTERN accepts a length outside PIN_MIN_DIGITS/PIN_MAX_DIGITS');
    }
}
/** Human-readable rule, used verbatim as the validation message. */
export const PIN_RULE_MESSAGE = `pin must be ${PIN_MIN_DIGITS} to ${PIN_MAX_DIGITS} digits`;
/** Does `pin` satisfy the policy? */
export function isValidPin(pin) {
    return PIN_PATTERN.test(pin);
}
/**
 * Trivially-guessable PINs. **Reported, NOT refused** — see below.
 *
 * A small space only helps if the PIN is actually drawn from it. `1234` and `0000`
 * are the first things an attacker tries, and a repeated or sequential run collapses
 * the space to a handful of candidates however long the PIN is — which matters MORE
 * at four digits, not less.
 *
 * ⚠️ THIS NO LONGER BLOCKS ANYTHING (Michael, 2026-09-01). It used to make `setPin`
 * throw. Employees keep the PINs they already have from the old clock and a real
 * list contains these, so refusing them would have blocked the import. `setPin` now
 * ACCEPTS them and emits an ops event instead; this predicate is what drives that
 * event and the import dry-run's report.
 *
 * Two consequences worth keeping in mind before reusing this function: a true
 * result is INFORMATIONAL, not a rejection reason, and the throttling is now the
 * only thing bounding PIN guessing — so it must not be weakened.
 *
 * Returns false for a mis-shaped PIN: that is a separate failure with its own
 * message, and reporting it as "weak" would tell the admin the wrong thing.
 */
export function isWeakPin(pin) {
    if (!isValidPin(pin)) {
        return false;
    }
    const digits = [...pin].map(Number);
    const first = digits[0];
    const allSame = digits.every((d) => d === first);
    const ascending = digits.every((d, i) => i === 0 || d === digits[i - 1] + 1);
    const descending = digits.every((d, i) => i === 0 || d === digits[i - 1] - 1);
    return allSame || ascending || descending;
}
//# sourceMappingURL=pin-policy.js.map